import React, { useEffect, useState, useMemo } from 'react'
import AdminLayout from '../../layouts/AdminLayout'
import {
    Box, Button, Flex, HStack,
    Table, Thead, Tbody, Tr, Th, Td, TableContainer,
    FormControl, FormLabel, Grid, Input, Select, IconButton,
} from "@chakra-ui/react";
import { FaEdit, FaFileCsv, FaFilePdf } from "react-icons/fa";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import adminService from '../../services/adminService';

import ReactPaginate from "react-paginate";
import { exportToPDF } from '../../utils/pdfExporter';
import { exportToCSV } from '../../utils/csvExporter';

const validationSchema = Yup.object().shape({
    Name: Yup.string().required("Name is required"),
    Rate: Yup.string().required("Rate is required"),
    Stat: Yup.string().required("Stat is required"),
});

const ITEMS_PER_PAGE = 10;

const Subscription = () => {

    const user = JSON.parse(localStorage.getItem("user"));

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        setValue
    } = useForm({
        resolver: yupResolver(validationSchema),
    });

    const [data, setData] = useState([])
    const [sId, setSID] = useState(0)

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(0);
    const [sortField, setSortField] = useState(null);
    const [sortOrder, setSortOrder] = useState("asc");

    useEffect(() => {
        subscriptionList();
    }, [])

    const subscriptionList = async () => {
        let param = { authToken: user.authToken }

        try {
            const res = await adminService.getSubscription(param)

            if (res.statusCode === 200) {
                setData(res.data)
            }
        } catch (error) {
            alert(error.response?.data?.message || "Something went wrong.");
        }
    }

    const handleConfig = async (data) => {
        data.sId = sId;
        data.authToken = user.authToken;
        try {
            const res = await adminService.saveSubscription(data)

            setSID(0)
            if (res.statusCode === 200) {
                alert(res.message);
                reset();
                subscriptionList();
            }
        } catch (error) {
            alert(error.response?.data?.message || "Something went wrong.");
        }
    }

    const handleEdit = (row) => {
        setValue("Name", row.Name);
        setValue("Rate", row.Rate);
        setValue("Stat", row.Stat);
        setSID(row.sId)

    };

    const filteredData = useMemo(() => {
        let result = [...data];
        if (search) {
            const lowerSearch = search.toLowerCase();
            result = result.filter((item) =>
            (`${item.Name}`.toLowerCase().includes(lowerSearch) ||
                String(item.Rate).includes(search.toLowerCase()) ||
                String(item.sId).includes(search.toLowerCase()) ||
                String(item.Stat).includes(lowerSearch))
            );
        }
        if (sortField) {
            result.sort((a, b) => {
                let aVal = a[sortField] ?? '';
                let bVal = b[sortField] ?? '';

                return sortOrder === "asc" ? aVal > bVal ? 1 : -1 : aVal < bVal ? 1 : -1;
            });
        }
        return result;
    }, [data, search, sortField, sortOrder]);

    const pageCount = Math.ceil(filteredData.length / ITEMS_PER_PAGE);

    const currentData = useMemo(() => {
        const start = page * ITEMS_PER_PAGE;
        return filteredData.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredData, page]);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortOrder(sortOrder === "asc" ? "desc" : "asc");
        } else {
            setSortField(field);
            setSortOrder("asc");
        }
    };

    const handlePageChange = ({ selected }) => {
        setPage(selected);
    };

    const exportCSV = () => {
        // data, filename
        exportToCSV(filteredData, "subscribe.csv")
    };

    const exportPDF = () => {
        const columns = [
            { label: "id", accessor: "sId" },
            { label: "Name", accessor: "Name" },
            { label: "Rate", accessor: "Rate" },
            { label: "Status", accessor: "Stat" },
        ];

        exportToPDF({
            data: filteredData,      // your actual data array
            columns,
            fileName: "subscribe.pdf"
        });
    };

    return (
        <AdminLayout>
            <Box maxW="1000px" mx="auto" px={4}>
                {/* Form Section */}
                <Box
                    bg="white"
                    p={6}
                    rounded="xl"
                    boxShadow="md"
                    mb={3}
                    border="1px solid"
                    borderColor="gray.200"
                >
                    <form onSubmit={handleSubmit(handleConfig)}>
                        <Grid
                            templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
                            gap={6}
                        >
                            <FormControl>
                                <FormLabel fontSize="sm">Name</FormLabel>
                                <Input type="text" {...register("Name")} size="sm" />
                                <Box color="red.500" fontSize="xs">{errors.Name?.message}</Box>
                            </FormControl>

                            <FormControl>
                                <FormLabel fontSize="sm">Rate</FormLabel>
                                <Input type="number" {...register("Rate")} size="sm" />
                                <Box color="red.500" fontSize="xs">{errors.Rate?.message}</Box>
                            </FormControl>

                            <FormControl>
                                <FormLabel fontSize="sm">Stat</FormLabel>
                                <Select {...register("Stat")} size="sm">
                                    <option value={5}>Enable</option>
                                    <option value={1}>Disable</option>
                                </Select>
                                <Box color="red.500" fontSize="xs">{errors.Stat?.message}</Box>
                            </FormControl>

                        </Grid>

                        <Button type="submit" colorScheme="blue" mt={6} size="sm">
                            Submit
                        </Button>
                    </form>
                </Box>

                {/* Table + Controls Section */}
                <Box
                    bg="white"
                    p={6}
                    rounded="xl"
                    boxShadow="md"
                    border="1px solid"
                    borderColor="gray.200"
                >
                    <Flex justify="space-between" mb={4} flexWrap="wrap" gap={4}>
                        <Input
                            placeholder="Search..."
                            value={search}
                            onChange={(e) => {
                                setSearch(e.target.value);
                                setPage(0);
                            }}
                            maxW="300px"
                            size="sm"
                        />
                        <HStack>
                            <Button
                                size="sm"
                                colorScheme="blue"
                                leftIcon={<FaFileCsv />}
                                onClick={exportCSV}
                            >
                                CSV
                            </Button>
                            <Button
                                size="sm"
                                colorScheme="red"
                                leftIcon={<FaFilePdf />}
                                onClick={exportPDF}
                            >
                                PDF
                            </Button>
                        </HStack>
                    </Flex>
                    <TableContainer overflowX="auto">
                        <Table size="sm" variant="simple">
                            <Thead bg="gray.100">
                                <Tr>
                                    <Th px={2} py={1} cursor="pointer" onClick={() => handleSort("sId")}>#</Th>
                                    <Th px={2} py={1} cursor="pointer" onClick={() => handleSort("Name")}>Name</Th>
                                    <Th px={2} py={1} cursor="pointer" onClick={() => handleSort("Rate")}>Rate</Th>
                                    <Th px={2} py={1} cursor="pointer" onClick={() => handleSort("Stat")}>Stat</Th>
                                    <Th px={2} py={1} >Action</Th>
                                </Tr>
                            </Thead>
                            <Tbody>
                                {currentData.map((row, index) => (
                                    <Tr key={row.sId}>
                                        <Td>{row.sId}</Td>
                                        <Td>{row.Name}</Td>
                                        <Td>{row.Rate}</Td>
                                        <Td>{row.Stat === 5 ? "Enable" : "Disable"}</Td>
                                        <Td>
                                            <IconButton
                                                icon={<FaEdit />}
                                                aria-label="Edit"
                                                borderRadius="full"
                                                onClick={() => handleEdit(row)}
                                            />
                                        </Td>
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>
                </Box>

                <Box mt={6}>
                    <ReactPaginate
                        previousLabel="Previous"
                        nextLabel="Next"
                        pageCount={pageCount}
                        onPageChange={handlePageChange}
                        containerClassName="pagination"
                        activeClassName="active"
                        pageClassName="page-item"
                        previousClassName="page-item"
                        nextClassName="page-item"
                        breakClassName="page-item"
                        pageLinkClassName="page-link"
                        previousLinkClassName="page-link"
                        nextLinkClassName="page-link"
                        breakLinkClassName="page-link"
                    />
                </Box>

                <style>{`
                                              .pagination {
                                                display: flex;
                                                justify-content: center;
                                                list-style: none;
                                                padding: 0;
                                                margin-top: 1rem;
                                              }
                                              .page-item {
                                                margin: 0 5px;
                                              }
                                              .page-link {
                                                padding: 8px 12px;
                                                border-radius: 6px;
                                                background-color: #e2e8f0;
                                                cursor: pointer;
                                              }
                                              .page-link:hover {
                                                background-color: #cbd5e0;
                                              }
                                              .active .page-link {
                                                background-color: #3182ce;
                                                color: white;
                                              }
                                            `}</style>

            </Box>
        </AdminLayout>
    )
}

export default Subscription
